import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, type ReactNode } from 'react';
import { setUnauthorizedListener } from '../lib/api';
import { sessionKey, useAttendee, useSignOut } from '../lib/queries';
import { SessionContext } from '../lib/session';

/**
 * Holds who is signed in. Nothing is kept in `localStorage` any more: the session cookie is
 * HttpOnly and `GET /auth/me` is the only source of truth, so a refunded registration (SYNC-04) or
 * a session the organizers revoked cannot be papered over by something this browser remembers.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const attendee = useAttendee();
  const signOut = useSignOut();

  useEffect(() => {
    setUnauthorizedListener(() => {
      // Only when the app currently believes somebody is signed in. A 401 with no session held is
      // the ordinary signed-out answer — including the one `GET /auth/me` gives on a first visit,
      // which would otherwise re-ask the question its own answer had just invalidated, forever.
      if (client.getQueryData(sessionKey) === undefined) {
        return;
      }

      // Removed rather than blanked: the API has stopped accepting this cookie, and the honest next
      // step is to ask again and be told so, rather than to keep a stale answer that looks signed in.
      client.removeQueries({ queryKey: sessionKey });
    });

    return () => setUnauthorizedListener(undefined);
  }, [client]);

  const value = useMemo(
    () => ({
      session: attendee.data ?? null,
      isPending: attendee.isPending,
      signOut: () => signOut.mutate(),
      isSigningOut: signOut.isPending,
    }),
    [attendee.data, attendee.isPending, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
